import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import moment from "moment-timezone";

import { useRoutes } from "utils/routes";
import CheckStatusBadge from "features/Checks/CheckStatusBadge";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import SQL from "ui/SQL";

import {
  ResolvedIssueList as ResolvedIssueListType,
  ResolvedIssueListVariables,
  ResolvedIssueList_getIssues as IssueType,
} from "./types/ResolvedIssueList";
import QUERY from "./Query.graphql";
import styles from "./style.module.scss";
import Grid from "ui/Grid";
import { makeFilter } from "utils/filter";
import { useQueryStringSearch } from "ui/QueryStringSearch";

type Props = {
  organizationSlug?: string;
  serverId?: string;
  databaseId?: string;
  checkGroupAndName: string;
  startTs?: number;
  endTs?: number;
};

const ISSUE_SEVERITY_TO_ORDER = {
  critical: 3,
  warning: 2,
  info: 1,
  okay: 0,
};

const ResolvedIssueList: React.FunctionComponent<Props> = ({
  organizationSlug,
  serverId,
  databaseId,
  checkGroupAndName,
  startTs,
  endTs,
}) => {
  const { server, database, serverIssue, databaseIssue } = useRoutes();
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const now = useMemo(() => moment(), []);
  const { data, loading, error } = useQuery<
    ResolvedIssueListType,
    ResolvedIssueListVariables
  >(QUERY, {
    variables: {
      organizationSlug,
      serverId,
      databaseId,
      checkGroupsAndNames: [checkGroupAndName],
      state: "resolved",
      startTs: startTs || now.clone().subtract(30, "d").unix(),
      endTs: endTs || now.unix(),
    },
  });
  if (loading || error || !data) {
    return <Loading error={!!error} />;
  }

  const issueUrl = (issue: IssueType): string => {
    return issue.database
      ? databaseIssue(issue.database.id, issue.id, checkGroupAndName)
      : serverIssue(issue.server.humanId, issue.id, checkGroupAndName);
  };

  const issues = data.getIssues
    .map((i) => ({
      serverName: i.server.name,
      databaseId: i.database?.id ?? "?",
      databaseName: i.database?.datname,
      severityOrder:
        ISSUE_SEVERITY_TO_ORDER[
          i.severity as keyof typeof ISSUE_SEVERITY_TO_ORDER
        ],
      triggeredAt: i.createdAt,
      resolvedAt: i.updatedAt,
      duration: i.updatedAt - i.createdAt,
      ...i,
    }))
    .filter(
      makeFilter(
        searchTerm,
        "severity",
        "serverName",
        "databaseName",
        "description",
        "queryText",
      ),
    );

  return (
    <Panel title="Resolved In Last 30 Days" secondaryTitle={<SearchInput />}>
      <Grid
        striped
        data={issues}
        defaultSortBy="resolvedAt"
        columns={[
          {
            field: "severityOrder",
            header: "Severity",
            width: "minmax(3%,96px)",
            renderer: function SeverityCell({ rowData }) {
              return <CheckStatusBadge status={rowData.severity} />;
            },
          },
          {
            field: "serverName",
            header: "Server",
            width: "minmax(5%,120px)",
            renderer: function ServerCell({ rowData, fieldData }) {
              return (
                <Link to={server(rowData.server.humanId)}>{fieldData}</Link>
              );
            },
          },
          {
            field: "databaseName",
            header: "Database",
            width: "minmax(5%,120px)",
            nullValue: "n/a",
            renderer: function DatabaseCell({ rowData, fieldData }) {
              return <Link to={database(rowData.databaseId)}>{fieldData}</Link>;
            },
          },
          {
            field: "description",
            header: "Description",
            width: "minmax(20%,1fr)",
            nullValue: "n/a",
            renderer: function DescriptionCell({ rowData, fieldData }) {
              const { queryText } = rowData;
              return (
                <Link to={issueUrl(rowData)}>
                  {fieldData}
                  {queryText && (
                    <div className={styles.issueQuery}>
                      <SQL inline sql={queryText} />
                    </div>
                  )}
                </Link>
              );
            },
          },
          {
            field: "triggeredAt",
            header: "First Triggered At",
            width: "minmax(10%,220px)",
            nullValue: "n/a",
            defaultSortOrder: "DESC",
            renderer: function TriggeredAtCell({ fieldData }) {
              return moment.unix(fieldData).format("ll LTS z");
            },
          },
          {
            field: "duration",
            header: "Duration",
            width: "minmax(5%,100px)",
            nullValue: "n/a",
            defaultSortOrder: "DESC",
            renderer: function DurationCell({ fieldData }) {
              return moment.duration(fieldData * 1000).humanize();
            },
          },
          {
            field: "resolvedAt",
            header: "Resolved At",
            width: "minmax(10%,220px)",
            nullValue: "n/a",
            defaultSortOrder: "DESC",
            renderer: function ResolvedAtCell({ fieldData }) {
              return moment.unix(fieldData).format("ll LTS z");
            },
          },
        ]}
      />
    </Panel>
  );
};

export default ResolvedIssueList;
